import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { LuCalculator, LuWallet, LuBanknote, LuReceiptText, LuMinus, LuPlus } from 'react-icons/lu';
import { getDefaultTransportPremium } from '../../configs/transportPremium';

const SalaryDetails = ({ serverResponse, calculationType, loading, country }) => {
    const { t } = useTranslation();
    const isNetCalculation = calculationType === 'net';
    const {
        net_salary,
        additional_salary,
        base_salary,
        gross_salary,
        total_contribution,
        transport_premium,
        prime_imposable,
        prime_non_imposable,
        autre_deduction
    } = serverResponse || {};

    // Anciens backends : la réponse ne renvoie pas le transport, on affiche
    // le défaut du pays.
    const transportValue = transport_premium !== undefined ? transport_premium : getDefaultTransportPremium(country);

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null) return '0 FCFA';
        return amount.toLocaleString() + ' FCFA';
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    // If no results yet, show placeholder
    if (!serverResponse) {
        return (
            <motion.div 
                className="h-full bg-white p-8 border border-gray-100 shadow-xl rounded-2xl flex flex-col justify-center items-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
            >
                <div className="text-center mb-8 max-w-md">
                    <div className="w-20 h-20 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
                        <LuCalculator className="h-8 w-8 text-orange-500" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('simulatorDetails.placeholder_heading')}</h3>
                    <p className="text-gray-600">
                        {t('simulatorDetails.placeholder_body')}
                    </p>

                    <div className="mt-8 pt-8 border-t border-dashed border-gray-200">
                        <p className="text-sm text-gray-500 mb-2">{t('simulatorDetails.placeholder_options_note')}</p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
                            <div className="flex items-center gap-2 bg-gradient-to-r from-orange-50 to-yellow-50 px-4 py-2 rounded-lg border border-orange-100 shadow-sm">
                                <LuBanknote className="w-5 h-5 text-orange-500" />
                                <span className="text-sm font-medium text-gray-700">{t('simulatorDetails.option_net')}</span>
                            </div>
                            <div className="flex items-center gap-2 bg-gradient-to-r from-orange-50 to-yellow-50 px-4 py-2 rounded-lg border border-orange-100 shadow-sm">
                                <LuWallet className="w-5 h-5 text-orange-500" />
                                <span className="text-sm font-medium text-gray-700">{t('simulatorDetails.option_brut')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    }

    // The actual detail items for the result. La prime imposable est placée
    // avant le brut (elle en fait partie), les éléments non imposables et la
    // déduction après les cotisations : le net se relit ligne à ligne.
    const salaryItems = [
        { label: t('simulatorDetails.item_base_salary'), value: base_salary || 0, icon: <LuBanknote className="w-5 h-5" /> },
        { label: t('simulatorDetails.item_additional_salary'), value: additional_salary || 0, icon: <LuPlus className="w-5 h-5" /> },
        ...(prime_imposable ? [{ label: t('simulatorDetails.item_prime_imposable'), value: prime_imposable, icon: <LuPlus className="w-5 h-5" /> }] : []),
        { label: t('simulatorDetails.item_gross_salary'), value: gross_salary || 0, isBold: true, isSubtotal: true, icon: <LuWallet className="w-5 h-5" /> },
        { label: t('simulatorDetails.item_total_contribution'), value: total_contribution || 0, isBold: true, isSubtotal: true, isDeduction: true, icon: <LuMinus className="w-5 h-5" /> },
        ...(transportValue ? [{ label: t('simulatorDetails.item_transport_bonus'), value: transportValue, icon: <LuPlus className="w-5 h-5" /> }] : []),
        ...(prime_non_imposable ? [{ label: t('simulatorDetails.item_prime_non_imposable'), value: prime_non_imposable, icon: <LuPlus className="w-5 h-5" /> }] : []),
        ...(autre_deduction ? [{ label: t('simulatorDetails.item_deduction'), value: autre_deduction, isDeduction: true, icon: <LuMinus className="w-5 h-5" /> }] : []),
        { label: t('simulatorDetails.item_net_salary'), value: net_salary || 0, isBold: true, isHighlighted: true, icon: <LuWallet className="w-5 h-5" /> }
    ];

    return (
        <motion.div 
            className="h-full bg-white p-6 md:p-8 border border-gray-100 shadow-xl rounded-2xl"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            <motion.div variants={itemVariants} className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-lg">
                        <LuReceiptText className="h-6 w-6 text-orange-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">
                        {isNetCalculation ? t('simulatorDetails.result_heading_net') : t('simulatorDetails.result_heading_brut')}
                    </h2>
                </div>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-gradient-to-br from-orange-50 to-yellow-50 p-6 rounded-xl border border-orange-100 shadow-sm mb-8">
                <div className="text-center">
                    <p className="text-gray-600 mb-2">
                        {isNetCalculation ? t('simulatorDetails.result_desc_net') : t('simulatorDetails.result_desc_brut')}
                    </p>
                    <h3 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">
                        {formatCurrency(isNetCalculation ? net_salary || 0 : additional_salary || 0)}
                    </h3>
                </div>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="w-1.5 h-5 bg-orange-500 rounded-full"></span>
                    {t('simulatorDetails.detail_heading')}
                </h3>
                
                <div className="space-y-3">
                    {salaryItems.map((item, index) => (
                        <motion.div 
                            key={item.label + index}
                            variants={itemVariants}
                            className={`
                                flex justify-between items-center p-3 rounded-lg transition-colors
                                ${item.isHighlighted ? 'bg-gradient-to-r from-orange-100 to-yellow-100 border border-orange-200' : 
                                  item.isSubtotal ? 'bg-gray-50 border border-gray-200' : 
                                  'hover:bg-gray-50'}
                            `}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`
                                    w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                                    ${item.isDeduction ? 'bg-red-100 text-red-600' : 
                                      item.isHighlighted ? 'bg-orange-100 text-orange-600' :
                                      item.isSubtotal ? 'bg-gray-100 text-gray-700' : 
                                      'bg-green-100 text-green-600'}
                                `}>
                                    {item.icon}
                                </div>
                                <span className={`
                                    ${item.isBold ? 'font-semibold' : ''}
                                    ${item.isDeduction ? 'text-red-700' : 
                                      item.isHighlighted ? 'text-orange-700' : 
                                      'text-gray-700'}
                                `}>
                                    {item.label}
                                </span>
                            </div>
                            <span className={`
                                ${item.isBold ? 'font-semibold' : ''}
                                ${item.isDeduction ? 'text-red-700' : 
                                  item.isHighlighted ? 'text-orange-700' : 
                                  item.isSubtotal ? 'text-gray-900' : 
                                  'text-gray-700'}
                            `}>
                                {item.isTextValue ? item.value : formatCurrency(item.value)}
                            </span>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-6 text-xs text-gray-500 text-center border-t border-gray-100 pt-6">
                <p>{t('simulatorDetails.footnote1')}</p>
                <p>{t('simulatorDetails.footnote2')}</p>
            </motion.div>
        </motion.div>
    );
};

export default SalaryDetails;
